import type {
    Dispatch,
    PropsWithChildren,
    ReactElement,
    SetStateAction,
} from 'react';
import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react';

import type { GetRawMaterialsQuery } from '@buying/library';

import { useQuery } from '@apollo/client/react';

import {
    GetExtendedRawMaterialsDocument,
    GetRawMaterialsDocument,
    GraphClientNames,
} from '@buying/library';

interface RawMaterialsContextType {
    data: GetRawMaterialsQuery | undefined;
    isLoading: boolean;
    generalQuery: string;
    fetchMore: () => void;
    setGeneralQuery: Dispatch<SetStateAction<string>>;
}

const RawMaterialsContext = createContext<RawMaterialsContextType | null>(null);

const useRawMaterials = (): RawMaterialsContextType => {
    const context = useContext(RawMaterialsContext);
    if (!context)
        throw new Error(
            'useRawMaterials must be used within a RawMaterialsProvider'
        );
    return context;
};

interface RawMaterialsProviderProps {
    materialSubcategoryPublicId: string | undefined;
    skip: boolean;
    isExtendedVersion?: boolean;
    onError: (error: Error, code: string) => void;
}

const pageSize = 100;

const RawMaterialsProvider = ({
    materialSubcategoryPublicId,
    skip,
    isExtendedVersion = false,
    children,
    onError: handleError,
}: PropsWithChildren<RawMaterialsProviderProps>): ReactElement => {
    const [generalQuery, setGeneralQuery] = useState<string>('');

    const query = isExtendedVersion
        ? GetExtendedRawMaterialsDocument
        : GetRawMaterialsDocument;

    const clientName = isExtendedVersion
        ? GraphClientNames['bestone-bi4-buying-ext-composition-bff']
        : GraphClientNames['bestone-bi4-buying-bom-bff'];

    const { data, error, loading, fetchMore } = useQuery(query, {
        variables: {
            materialSubcategoryPublicId: materialSubcategoryPublicId ?? '',
            generalQuery,
            isDeleted: false,
            pagination: {
                first: pageSize,
            },
        },
        skip: skip || !materialSubcategoryPublicId,
        fetchPolicy: 'network-only',
        nextFetchPolicy: 'cache-first',
        context: { clientName },
    });

    const fetchMoreRawMaterials = useCallback(() => {
        if (data?.rawMaterials?.pageInfo?.hasNextPage) {
            void fetchMore({
                variables: {
                    pagination: {
                        first: pageSize,
                        after: data.rawMaterials.pageInfo.endCursor,
                    },
                },
                updateQuery: (prev, { fetchMoreResult }) => {
                    if (!fetchMoreResult) return prev;
                    return {
                        rawMaterials: {
                            __typename: prev.rawMaterials?.__typename,
                            totalCount: prev.rawMaterials?.totalCount || 0,
                            edges: [
                                ...(prev.rawMaterials?.edges || []),
                                ...(fetchMoreResult.rawMaterials?.edges || []),
                            ],
                            pageInfo: fetchMoreResult.rawMaterials?.pageInfo,
                        },
                    };
                },
            });
        }
    }, [
        data?.rawMaterials?.pageInfo?.endCursor,
        data?.rawMaterials?.pageInfo?.hasNextPage,
        fetchMore,
    ]);

    const contextValue = useMemo(
        (): RawMaterialsContextType => ({
            data,
            isLoading: loading,
            fetchMore: fetchMoreRawMaterials,
            generalQuery,
            setGeneralQuery,
        }),
        [loading, fetchMoreRawMaterials, generalQuery, data]
    );

    useEffect(() => {
        if (error) {
            handleError(error, 'queryRawMaterialsFailed');
        }
    }, [error, handleError]);

    return (
        <RawMaterialsContext.Provider value={contextValue}>
            {children}
        </RawMaterialsContext.Provider>
    );
};

export { RawMaterialsProvider, useRawMaterials };
export type { RawMaterialsProviderProps };
